import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import { FaUser, FaClock, FaEnvelope, FaCircle } from 'react-icons/fa';

const EditorMessages = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    const requestsRef = collection(db, 'requests');
    const q = query(requestsRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const requestList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        lastMessage: null,
        unreadCount: 0
      }));

      // Her istek için son mesajı ve okunmamış mesaj sayısını al
      requestList.forEach(request => {
        const messagesRef = collection(db, 'requests', request.id, 'messages');
        const messagesQuery = query(messagesRef, orderBy('createdAt', 'desc'));
        
        onSnapshot(messagesQuery, (messagesSnapshot) => {
          const messages = messagesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          setRequests(prevRequests => {
            const updatedRequests = [...prevRequests];
            const index = updatedRequests.findIndex(r => r.id === request.id);
            
            if (index !== -1) {
              updatedRequests[index] = {
                ...updatedRequests[index],
                lastMessage: messages[0] || null,
                unreadCount: messages.filter(m => !m.read && m.senderId !== user.uid).length
              };
            }
            
            return updatedRequests;
          });
        });
      });

      setRequests(requestList);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Mesajlar</h1>
      
      <div className="grid gap-4">
        {requests.map((request) => (
          <Link
            key={request.id}
            to={`/editor/requests/${request.id}`}
            className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-4"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {request.title}
                  </h3>
                  {request.unreadCount > 0 && (
                    <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                      {request.unreadCount}
                    </span>
                  )}
                </div>
                
                <div className="flex items-center text-sm text-gray-600 mt-1 space-x-4">
                  <div className="flex items-center">
                    <FaUser className="mr-1" />
                    <span>{request.userEmail}</span>
                  </div>
                  <div className="flex items-center">
                    <FaClock className="mr-1" />
                    <span>
                      {request.createdAt?.toDate().toLocaleDateString('tr-TR')}
                    </span>
                  </div>
                  <span className={`flex items-center px-2 py-1 rounded-full text-xs ${
                    request.status === 'completed' ? 'bg-green-100 text-green-800' :
                    request.status === 'in-progress' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {request.status === 'completed' ? 'Tamamlandı' :
                     request.status === 'in-progress' ? 'İşleniyor' :
                     'Beklemede'}
                  </span>
                </div>

                {request.lastMessage && (
                  <div className="mt-2 text-sm text-gray-600">
                    <div className="flex items-center space-x-2">
                      <FaEnvelope className="text-gray-400" />
                      <span className="font-medium">{request.lastMessage.senderName}:</span>
                      <span className="truncate">{request.lastMessage.content}</span>
                      <span className="text-xs text-gray-500">
                        {request.lastMessage.createdAt?.toDate().toLocaleTimeString('tr-TR')}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-2">
                {request.unreadCount > 0 && (
                  <FaCircle className="text-blue-500 text-xs" />
                )}
              </div>
            </div>
          </Link>
        ))}

        {requests.length === 0 && (
          <div className="text-center py-8 bg-white rounded-lg">
            <FaEnvelope className="mx-auto text-4xl text-gray-400 mb-4" />
            <p className="text-gray-600">Henüz hiç mesaj yok</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditorMessages; 